/*============================================================================
Digital Products Validation (Cart & Product Pages)
==============================================================================*/

(function() {
  // Configuration
  const CONFIG = {
    endpoints: {
      digitalCollections: [
        "/collections/online-termine/products.json",
        "/collections/lizenzen/products.json"
      ],
      cart: "/cart.json"
    },
    selectors: {
      // Cart selectors
      cartCheckoutBtns: '[name="checkout"], .checkout-button, .cart__checkout, .cart__submit, [href="/checkout"]',
      cartFooter: '.cart__footer--buttons, .drawer__footer-buttons',
      cartMessageWarning: ".cart-message-warnung",
      cartMessageAngebot: ".cart-message-angebot",
      specificSection: "#shopify-section-template--21053895868683__1734644082ce0770bf",
      // New selectors for shipping and BOGOS
      freeShippingGoal: "free-shipping-goal",
      bogosSection: '[id^="shopify-section-template--"][id$="__1734644082ce0770bf"]',
      customLiquidSection: '#shopify-section-template--24399761637643__custom_liquid_gEHKhH',
      
      // Product page selectors 
      productForm: '.product-form:not([id*="quickview"])',
      addToCartButton: 'button[name="add"]',
      stickyBar: '.sticky-atc-bar',
      warningClass: 'product-webinar-warning',
      quickviewContainer: '[id*="quickview"], [class*="quickview"], [id*="quick-view"], [class*="quick-view"], [data-view-mode="quick-view"]'
    },
    messages: {
      cartError: "Aus technischen und steuerlichen Gründen können digitale Produkte (Live-Webinare und Lizenzen) nicht gemeinsam mit physischen Produkten erworben werden.",
      digitalWithPhysical: "Du hast bereits ein physisches Produkt in deinem Warenkorb. Aus technischen und steuerlichen Gründen können Live-Webinare und Lizenzen nicht gemeinsam mit physischen Produkten erworben werden. Bitte schließe erst die offene Bestellung ab, bevor du digitale Produkte in den Warenkorb legst.",
      physicalWithDigital: "Du hast bereits ein Live-Webinar oder Lizenz in deinem Warenkorb. Aus technischen und steuerlichen Gründen können digitale Produkte nicht gemeinsam mit physischen Produkten erworben werden. Bitte schließe erst die offene Bestellung ab, bevor du physische Produkte in den Warenkorb legst."
    }
  };
  
  // Add CSS to hide elements immediately while JS is loading
  function addPreventiveCSS() {
    // Create a style element
    const style = document.createElement('style');
    style.id = 'digital-products-preventive-css';
    style.innerHTML = `
      /* Hide these elements initially until we can determine if they should be shown */
      .cart-message-angebot,
      free-shipping-goal,
      [id^="shopify-section-template--"][id$="__1734644082ce0770bf"] {
        transition: opacity 0.1s;
        opacity: 0;
      }
    `;
    document.head.appendChild(style);
  }
  
  // Call this immediately to prevent flashing
  addPreventiveCSS();
  
  // Utility functions
  const utils = {
    isProductPage() {
      return window.location.pathname.includes("/products/");
    },
    
    async fetchJson(url) {
      try {
        const response = await fetch(url);
        if (!response.ok) throw new Error(`HTTP error ${response.status}`);
        return await response.json();
      } catch (error) {
        console.error(`Error fetching ${url}:`, error);
        return null;
      }
    },
    
    getElements(selector) {
      return document.querySelectorAll(selector);
    },
    
    createElement(tag, className, text) {
      const element = document.createElement(tag);
      if (className) element.className = className;
      if (text) element.textContent = text;
      return element;
    },
    
    toggleElements(selector, isVisible) {
      this.getElements(selector).forEach(el => {
        el.style.display = isVisible ? "" : "none";
      });
    },
    
    insertBefore(newNode, referenceNode) {
      if (referenceNode && referenceNode.parentNode) {
        referenceNode.parentNode.insertBefore(newNode, referenceNode);
        return true;
      }
      return false;
    },
    
    insertAfter(newNode, referenceNode) {
      if (referenceNode && referenceNode.parentNode) {
        referenceNode.parentNode.insertBefore(newNode, referenceNode.nextSibling);
        return true;
      }
      return false;
    },
    
    getProductId(container = document) {
      // Try various methods to find the product ID
      
      // From container data attribute
      if (container !== document) {
        const productInfo = container.closest('[data-product-id]');
        if (productInfo?.dataset.productId) {
          return parseInt(productInfo.dataset.productId, 10);
        }
        
        // From form input
        const hiddenInput = container.querySelector('input[name="product-id"]');
        if (hiddenInput?.value) {
          return parseInt(hiddenInput.value, 10);
        }
      }
      
      // From meta tag
      const productMeta = document.querySelector('meta[property="product:id"]');
      if (productMeta?.content) {
        return parseInt(productMeta.content, 10);
      }
      
      // From main product input
      const mainProductInput = document.querySelector('input[name="product-id"]');
      if (mainProductInput?.value) {
        return parseInt(mainProductInput.value, 10);
      }
      
      return null;
    }
  };

  // Digital Products Manager - No caching, direct fetch when needed
  const digitalProductsManager = {
    digitalProductIds: null,
    
    async getDigitalProductIds() {
      // If we've already fetched the digital products, return them
      if (this.digitalProductIds) {
        return this.digitalProductIds;
      }
      
      try {
        // Fetch all digital collections in parallel
        const responses = await Promise.all(
          CONFIG.endpoints.digitalCollections.map(url => utils.fetchJson(url))
        );
        
        // Create a new Set for the digital product IDs
        const digitalProductIds = new Set();
        
        // Process responses
        for (const data of responses) {
          if (data?.products && Array.isArray(data.products)) {
            data.products.forEach(product => digitalProductIds.add(product.id));
          }
        }
        
        // Save the result
        this.digitalProductIds = digitalProductIds;
        return digitalProductIds;
        
      } catch (error) {
        console.error("Error fetching digital products:", error);
        return new Set(); // Return empty set on error
      }
    },
    
    async isDigitalProduct(productId) {
      if (!productId) return false;
      const id = typeof productId === 'string' ? parseInt(productId, 10) : productId;
      const digitalProducts = await this.getDigitalProductIds();
      return digitalProducts.has(id);
    }
  };
  
  // Cart Manager
  const cartManager = {
    async getCartContents() {
      const cartData = await utils.fetchJson(CONFIG.endpoints.cart);
      
      if (!cartData?.items || cartData.items.length === 0) {
        return { hasDigitalProducts: false, hasPhysicalProducts: false };
      }
      
      const digitalProducts = await digitalProductsManager.getDigitalProductIds();
      
      // Check cart items
      let hasDigitalProducts = false;
      let hasPhysicalProducts = false;
      
      for (const item of cartData.items) {
        if (digitalProducts.has(item.product_id)) {
          hasDigitalProducts = true;
        } else {
          hasPhysicalProducts = true;
        }
        
        // Short-circuit if we already found both types
        if (hasDigitalProducts && hasPhysicalProducts) break;
      }
      
      return { hasDigitalProducts, hasPhysicalProducts };
    },
    
    toggleCheckoutButtons(enabled) {
      utils.getElements(CONFIG.selectors.cartCheckoutBtns).forEach(button => {
        if (button.tagName === "A") {
          button.style.pointerEvents = enabled ? "" : "none";
          button.style.opacity = enabled ? "" : "0.5";
        } else {
          button.disabled = !enabled;
        }
      });
    },
    
    removeWarnings() {
      utils.getElements(CONFIG.selectors.cartMessageWarning).forEach(el => el.remove());
    },
    
    addWarningToCart() {
      this.removeWarnings(); // Remove existing warnings first
      
      const warning = utils.createElement("div", 
        CONFIG.selectors.cartMessageWarning.substring(1), // Remove the dot
        CONFIG.messages.cartError
      );
      
      // Add to all cart footers
      utils.getElements(CONFIG.selectors.cartFooter).forEach(footer => {
        if (footer && !footer.querySelector(CONFIG.selectors.cartMessageWarning)) {
          footer.insertBefore(warning.cloneNode(true), footer.firstChild);
        }
      });
    },
    
    // Hide shipping-related elements when digital products are in cart
    toggleShippingElements(hasDigitalProducts) {
      // Remove the preventive CSS once we know the state
      const preventiveCSS = document.getElementById('digital-products-preventive-css');
      if (preventiveCSS) {
        preventiveCSS.remove();
      }
      
      // Hide cart message angebot
      const cartMessageAngebotElements = document.querySelectorAll(CONFIG.selectors.cartMessageAngebot);
      cartMessageAngebotElements.forEach(element => {
        if (hasDigitalProducts) {
          element.style.display = "none";
        } else {
          element.style.display = "";
          element.style.opacity = "1";
        }
      });
      
      // Hide shipping rates section
      const shippingRateElements = document.querySelectorAll('.cart__footer--shipping-rate');
      shippingRateElements.forEach(element => {
        element.style.display = hasDigitalProducts ? "none" : "";
      });
      
      // Hide free shipping goal elements
      const freeShippingElements = document.querySelectorAll(CONFIG.selectors.freeShippingGoal);
      freeShippingElements.forEach(element => {
        if (hasDigitalProducts) {
          element.style.display = "none";
        } else {
          element.style.display = "";
          element.style.opacity = "1";
        }
      });
      
      // Hide BOGOS section
      const bogosSections = document.querySelectorAll(CONFIG.selectors.bogosSection);
      bogosSections.forEach(section => {
        if (hasDigitalProducts) {
          section.style.display = "none";
        } else {
          section.style.display = "";
          section.style.opacity = "1";
        }
      });

      // Hide custom liquid section
      const customLiquidSection = document.querySelector(CONFIG.selectors.customLiquidSection);
      if (customLiquidSection) {
        if (hasDigitalProducts) {
          customLiquidSection.style.display = "none";
        } else {
          customLiquidSection.style.display = "";
          customLiquidSection.style.opacity = "1";
        }
      }
    },
    
    async validateCart() {
      try {
        const { hasDigitalProducts, hasPhysicalProducts } = await this.getCartContents();
        
        // Hide shipping-related elements when digital products are in cart
        this.toggleShippingElements(hasDigitalProducts);
        
        // Handle mixed cart (digital + physical products)
        if (hasDigitalProducts && hasPhysicalProducts) {
          this.addWarningToCart();
          this.toggleCheckoutButtons(false);
        } else {
          this.removeWarnings();
          this.toggleCheckoutButtons(true);
        }
      } catch (error) {
        console.error("Error validating cart:", error);
      }
    }
  };
  
  // Product Page Manager
  const productPageManager = {
    handleProductForms(forms, shouldDisable, warningMessage) {
      forms.forEach(form => {
        const addButton = form.querySelector(CONFIG.selectors.addToCartButton);
        if (!addButton) return;
        
        // Handle warning message
        const existingWarning = form.querySelector('.' + CONFIG.selectors.warningClass);
        
        if (shouldDisable) {
          // Add warning if needed
          if (!existingWarning) {
            const warning = utils.createElement('div', 
              `${CONFIG.selectors.warningClass} cart-message-warnung`, 
              warningMessage
            );
            
            const buttonContainer = addButton.closest('.product-form__buttons') || addButton.parentNode;
            utils.insertAfter(warning, buttonContainer);
          }
          
          // Disable button
          addButton.setAttribute('disabled', 'disabled');
          addButton.style.opacity = '0.5';
          addButton.style.cursor = 'not-allowed';
        } else {
          // Remove warning if it exists
          if (existingWarning) existingWarning.remove();
          
          // Enable button
          addButton.removeAttribute('disabled');
          addButton.style.opacity = '';
          addButton.style.cursor = '';
        }
      });
    },
    
    toggleStickyBar(visible) {
      const stickyBar = document.querySelector(CONFIG.selectors.stickyBar);
      if (stickyBar) {
        stickyBar.style.display = visible ? '' : 'none';
      }
    },
    
    async validateProductForm(container, cartContents) {
      const productId = utils.getProductId(container);
      if (!productId) return { shouldWarn: false };
      
      const { hasDigitalProducts, hasPhysicalProducts } = cartContents;
      const isDigital = await digitalProductsManager.isDigitalProduct(productId);
      
      // Determine if we need to show a warning
      let shouldWarn = false;
      let warningMessage = '';
      
      if (isDigital && hasPhysicalProducts) {
        shouldWarn = true;
        warningMessage = CONFIG.messages.digitalWithPhysical;
      } else if (!isDigital && hasDigitalProducts) {
        shouldWarn = true;
        warningMessage = CONFIG.messages.physicalWithDigital;
      }
      
      return { shouldWarn, warningMessage };
    },
    
    async validateProductPage() {
      try {
        const cartContents = await cartManager.getCartContents();
        
        // Process main product forms
        const mainForms = utils.getElements(`:not(${CONFIG.selectors.quickviewContainer}) ${CONFIG.selectors.productForm}`);
        
        // Get validation result for the main product
        const mainResult = await this.validateProductForm(document, cartContents);
        
        // Apply validation to main product forms
        this.handleProductForms(mainForms, mainResult.shouldWarn, mainResult.warningMessage);
        
        // Toggle sticky bar visibility
        this.toggleStickyBar(!mainResult.shouldWarn);
        
        // Process quickview containers
        const quickviewContainers = utils.getElements(CONFIG.selectors.quickviewContainer);
        
        for (const container of quickviewContainers) {
          const forms = container.querySelectorAll('.product-form');
          if (!forms.length) continue;
          
          // Get validation result for this quickview
          const quickviewResult = await this.validateProductForm(container, cartContents);
          
          // Apply validation to quickview forms
          this.handleProductForms(forms, quickviewResult.shouldWarn, quickviewResult.warningMessage);
        }
      } catch (error) {
        console.error("Error validating product page:", error);
      }
    }
  };
  
  // Event listeners and initializer
  const eventManager = {
    cartEvents: ["cart:updated", "cart:refresh"],
    
    addListener(event, callback) {
      document.addEventListener(event, callback);
    },
    
    init() {
      // Set up cart validation (runs on all pages)
      const cartValidation = cartManager.validateCart.bind(cartManager);
      this.cartEvents.forEach(event => this.addListener(event, cartValidation));
      
      // Add drawer listener
      this.addListener("drawer:handleAfterShow", e => {
        if (e.target.hasAttribute("cart-drawer")) {
          cartManager.validateCart();
        }
      });
      
      // Run initial cart validation
      cartManager.validateCart();
      
      // Only set up product page validation on product pages
      if (utils.isProductPage()) {
        const productValidation = productPageManager.validateProductPage.bind(productPageManager);
        
        // Add product validation to cart events
        this.cartEvents.forEach(event => this.addListener(event, productValidation));
        
        // Run initial product validation
        productValidation();
        
        // Watch for dynamically loaded quickviews
        const observer = new MutationObserver((mutations) => {
          for (const mutation of mutations) {
            if (mutation.addedNodes.length) {
              const quickviews = [...mutation.addedNodes].filter(node => 
                node.nodeType === 1 && // Element node
                (node.matches?.(CONFIG.selectors.quickviewContainer) || 
                 node.querySelector?.(CONFIG.selectors.quickviewContainer))
              );
              
              if (quickviews.length) {
                productValidation();
                break;
              }
            }
          }
        });
        
        observer.observe(document.body, { childList: true, subtree: true });
      }
      
      // Set up a MutationObserver to watch for dynamically added shipping elements
      const shippingObserver = new MutationObserver(async (mutations) => {
        // Check if any of the mutations involve our target elements
        const targetElementAdded = mutations.some(mutation => {
          return Array.from(mutation.addedNodes).some(node => {
            if (node.nodeType !== 1) return false; // Not an element node
            
            // Check if this is one of our target elements or contains them
            return (
              node.classList?.contains('cart-message-angebot') ||
              node.tagName?.toLowerCase() === 'free-shipping-goal' ||
              node.querySelector?.('.cart-message-angebot, free-shipping-goal') ||
              node.id?.includes('1734644082ce0770bf')
            );
          });
        });
        
        if (targetElementAdded) {
          const { hasDigitalProducts } = await cartManager.getCartContents();
          if (hasDigitalProducts) {
            cartManager.toggleShippingElements(true);
          }
        }
      });
      
      // Observe the entire document for changes
      shippingObserver.observe(document.body, {
        childList: true,
        subtree: true
      });
    }
  };
  
  // Initialize when DOM is ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => eventManager.init());
  } else {
    eventManager.init();
  }
})(); 